// frontend/src/pages/Login/LoginPage.js

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaEnvelope, FaLock, FaArrowLeft, FaEye, FaEyeSlash, FaUserShield, FaBriefcase, FaUserCheck } from 'react-icons/fa';
import './LoginPage.css';
import { supabase } from '../../supabaseClient';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [remember, setRemember] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  // Usuarios demo para mostrar en la UI
  const demoUsers = [
    { email: 'admin@demo.com', role: 'Administrador', icon: <FaUserShield className="text-sm text-amber-400" /> },
    { email: 'jefe@demo.com', role: 'Jefe de Proyecto', icon: <FaBriefcase className="text-sm text-blue-400" /> },
    { email: 'colaborador@demo.com', role: 'Colaborador', icon: <FaUserCheck className="text-sm text-emerald-400" /> }
  ];

  // Auto-llenar credenciales demo
  const fillDemoCredentials = (demoEmail) => {
    setEmail(demoEmail);
    setPassword('demo123');
    setError(null);
  };

  // Validación de email
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validación de campos
  const validateForm = () => {
    if (!email.trim()) {
      setError('El correo electrónico es requerido');
      return false;
    }
    if (!validateEmail(email)) {
      setError('Por favor ingresa un correo electrónico válido');
      return false;
    }
    if (!password) {
      setError('La contraseña es requerida');
      return false;
    }
    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return false;
    }
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading) return;

    setError(null);
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: email,
        password: password,
      });

      if (error) {
        throw error;
      }

      if (remember) {
        localStorage.setItem('cf_remember', '1');
      } else {
        localStorage.removeItem('cf_remember');
      }
      setLoading(false);
      navigate('/app-loader');

    } catch (err) {
      console.error('Error de login:', err.message);
      setError(err.message || 'Email o contraseña incorrectos.');
      setLoading(false);
    }
  };

  return (
    <div className="login-page-container min-h-screen bg-slate-950 flex items-center justify-center p-4 sm:p-6 relative overflow-hidden font-sans">
      {/* Background Decorative Mesh & Glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="bg-bubbles">
        <span />
        <span />
        <span />
        <span />
        <span />
      </div>
      
      {/* Container principal con dos columnas */}
      <div className="relative z-10 w-full max-w-4xl mx-auto flex flex-col lg:flex-row items-center justify-center gap-8">
        
        {/* Tarjetas de usuarios demo - Lado izquierdo */}
        <motion.div
          className="w-full lg:w-80 order-2 lg:order-1"
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="text-center lg:text-left mb-4">
            <span className="inline-flex items-center gap-2 text-slate-400 text-xs font-semibold uppercase tracking-wider">
              <span className="h-px w-4 bg-slate-700"></span>
              Cuentas Demo de Acceso Directo
              <span className="h-px w-4 bg-slate-700"></span>
            </span>
          </div>
          
          <div className="space-y-3">
            {demoUsers.map((user, index) => (
              <motion.button
                key={user.email}
                type="button"
                onClick={() => fillDemoCredentials(user.email)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 + index * 0.1 }}
                className="w-full group bg-slate-900/70 backdrop-blur-md border border-slate-800 rounded-2xl p-4 text-left hover:bg-slate-800/80 hover:border-slate-700 transition-all duration-300 shadow-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center shadow-inner group-hover:scale-105 transition-transform">
                    {user.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-semibold truncate group-hover:text-blue-400 transition-colors">
                      {user.email}
                    </p>
                    <p className="text-slate-400 text-xs mt-0.5">
                      {user.role}
                    </p>
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
          
          <div className="mt-4 text-center">
            <p className="text-slate-400 text-xs">
              Contraseña por defecto: <code className="text-amber-400 bg-slate-900 border border-slate-800 px-2 py-0.5 rounded-md font-mono">demo123</code>
            </p>
          </div>
        </motion.div>

        {/* Formulario de Login - Centro/Derecha */}
        <motion.div
          className="w-full max-w-md order-1 lg:order-2"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Botón Volver */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 mb-6 text-slate-400 hover:text-white text-sm font-medium transition-colors"
          >
            <FaArrowLeft className="text-xs" />
            <span>Volver al inicio</span>
          </Link>

          {/* Card de Login */}
          <div className="bg-white rounded-3xl shadow-2xl p-8 border border-slate-100">
            {/* Logo y Título */}
            <div className="text-center mb-8">
              <span className="text-2xl font-extrabold text-slate-900 tracking-tight block mb-1">CommitFlow</span>
              <h1 className="text-xl font-bold text-slate-800 tracking-tight">
                Iniciar sesión
              </h1>
              <p className="text-slate-500 text-sm mt-1">
                Accede a tu cuenta de CommitFlow
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Mensaje de Error */}
              <AnimatePresence>
                {error && (
                  <motion.div 
                    className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-600 text-sm font-medium"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    {error}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Email Input */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-1.5">
                  Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <FaEnvelope className="text-slate-400 text-sm" />
                  </div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="correo@ejemplo.com"
                    required
                    disabled={loading}
                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 transition-all disabled:opacity-60"
                  />
                </div>
              </div>
              
              {/* Password Input */}
              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-slate-700 mb-1.5">
                  Contraseña
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <FaLock className="text-slate-400 text-sm" />
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    autoComplete="off"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    disabled={loading}
                    className="w-full pl-10 pr-10 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 transition-all disabled:opacity-60"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    disabled={loading}
                    className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <FaEyeSlash className="text-sm" /> : <FaEye className="text-sm" />}
                  </button>
                </div>
              </div>

              {/* Remember */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={remember} 
                    onChange={(e) => setRemember(e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500/20"
                  />
                  <span className="text-slate-600 text-sm font-medium">
                    Mantener sesión iniciada
                  </span>
                </label>
              </div>

              {/* Submit Button */}
              <button 
                type="submit" 
                disabled={loading}
                className="w-full py-3 px-4 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold rounded-xl transition-all shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Ingresando...</span>
                  </>
                ) : (
                  'Ingresar'
                )}
              </button>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
